import { AnimatePresence } from "framer-motion";
import { ArrowDownRight, GitBranch, Merge, Pencil, RotateCcw, ShieldAlert, Snowflake, Trash2, Undo2, XCircle } from "lucide-react";
import { useState, type FormEvent } from "react";
import type { Hypothesis, ManualHypothesisStatus } from "@lovmia/shared";
import { ApiError } from "../api";
import { useAppState } from "../state";
import { Button, ConfirmDialog, Menu, Modal, Select, type MenuAction } from "./Ui";

/**
 * Управление гипотезой из списка: переименовать, подвинуть статус, удалить.
 *
 * Один компонент на все три витрины (карточки обоих видов доски и строка
 * реестра) — чтобы набор действий и их правила не разъезжались по трём копиям.
 * Всё это решения по концепту, поэтому меню видит только admin; у остальных
 * компонент не рисуется вовсе, а сервер всё равно проверяет роль сам.
 */
export function HypothesisControls({ family, onDeleted, className }: {
  family: Hypothesis;
  /** Зовётся после удаления — закрыть открытую карточку этой гипотезы. */
  onDeleted?: () => void;
  className?: string;
}) {
  const { data, act, notify } = useAppState();
  const [dialog, setDialog] = useState<"edit" | "delete" | "merge" | "backlog" | "lost" | "variation" | null>(null);
  const [busy, setBusy] = useState(false);
  if (data.user.role !== "admin") return null;

  const setStatus = async (status: ManualHypothesisStatus, success: string) => {
    try {
      await act(`/api/hypotheses/${family.id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }, success);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось изменить статус", undefined, "danger");
    }
  };

  const returnToBacklog = async () => {
    setBusy(true);
    try {
      await act(`/api/hypotheses/${family.id}/backlog`, { method: "POST", body: JSON.stringify({}) }, `${family.id} вернулась в бэклог`);
      setDialog(null);
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "Не удалось вернуть гипотезу в бэклог", undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  const markLost = async () => {
    setBusy(true);
    try {
      await act(`/api/hypotheses/${family.id}/lost`, { method: "POST", body: JSON.stringify({}) }, `${family.id} в лузерах`);
      setDialog(null);
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "Не удалось перевести гипотезу в лузеры", undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await act(`/api/hypotheses/${family.id}`, { method: "DELETE" }, `${family.id} удалена`);
      setDialog(null);
      onDeleted?.();
    } catch (error) {
      // 409 — не сбой, а правило: у гипотезы уже есть следы в кабинете.
      // Текст приходит с сервера и объясняет, что делать вместо удаления.
      notify(error instanceof ApiError ? error.message : "Не удалось удалить гипотезу", undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  const actions: MenuAction[] = [
    { label: "Переименовать и править", icon: <Pencil size={14} />, onSelect: () => setDialog("edit") },
    // Следующий шаг линии: переработанное утверждение мерится заново, но
    // остаётся привязанным к тому, из чего выросло.
    { label: "Сделать вариацию…", icon: <GitBranch size={14} />, onSelect: () => setDialog("variation") }
  ];
  if (family.status !== "rework_safe") {
    // Не стоп и не смерть концепта: механика живая, но текущее исполнение
    // слишком рискованное — гипотеза ждёт в бэклоге безопасной переделки.
    actions.push({ label: "Сделать сейвовее", icon: <ShieldAlert size={14} />, onSelect: () => void setStatus("rework_safe", `${family.id} в бэклоге — сделать сейвовее`) });
  }
  if (family.status !== "frozen") {
    actions.push({ label: "В холодильник", icon: <Snowflake size={14} />, onSelect: () => void setStatus("frozen", `${family.id} в холодильнике`) });
  }
  if (family.status !== "killed") {
    actions.push({ label: "Закрыть", icon: <XCircle size={14} />, onSelect: () => void setStatus("killed", `${family.id} закрыта`) });
  }
  // Лузер — финал концепта, зеркальный виннеру. С виннером в семье пункта нет:
  // утверждение уже подтвердилось (сервер проверит то же самое).
  const hasWinner = family.creatives.some((creative) => creative.state === "scaled");
  if (family.creatives.length > 0 && family.status !== "lost" && !hasWinner) {
    actions.push({ label: "В лузеры…", icon: <ArrowDownRight size={14} />, tone: "danger", onSelect: () => setDialog("lost") });
  }
  if (family.status === "frozen" || family.status === "killed" || family.status === "rework_safe" || family.status === "lost") {
    actions.push({ label: "Вернуть в работу", icon: <RotateCcw size={14} />, onSelect: () => void setStatus("backlog", `${family.id} снова в работе`) });
  }
  // Весь заход целиком назад в бэклог: крео уходят в архив вместе со своими
  // цифрами, гипотеза снова ждёт волну. Пункт нужен, только пока в семье есть
  // что закрывать.
  if (family.creatives.length > 0 && family.status !== "backlog") {
    actions.push({ label: "Вернуть в бэклог…", icon: <Undo2 size={14} />, onSelect: () => setDialog("backlog") });
  }
  // Объединение: крео этой гипотезы переезжают в другую, эта исчезает.
  // Возможно, пока ни одно её крео не измерено — сервер проверит то же самое.
  const mergeable = family.creatives.every((creative) => creative.metrics.spend === 0 && !creative.verdict);
  if (mergeable) {
    actions.push({ label: "Объединить с другой гипотезой…", icon: <Merge size={14} />, onSelect: () => setDialog("merge") });
  }
  actions.push({ label: "Удалить", icon: <Trash2 size={14} />, tone: "danger", onSelect: () => setDialog("delete") });

  return <>
    <Menu actions={actions} className={className} label={`Действия по ${family.id}`} />
    <AnimatePresence>
      {dialog === "edit" && <EditHypothesis family={family} close={() => setDialog(null)} />}
      {dialog === "variation" && <CreateVariation family={family} close={() => setDialog(null)} />}
      {dialog === "merge" && <MergeHypothesis family={family} close={() => setDialog(null)} onMerged={onDeleted} />}
      {dialog === "backlog" && <ConfirmDialog
        title={`Вернуть ${family.id} в бэклог?`}
        confirmLabel="Вернуть в бэклог"
        loading={busy}
        onConfirm={returnToBacklog}
        onClose={() => setDialog(null)}
        description={<>
          <p><strong>{family.title ?? family.statement}</strong></p>
          <p>Этот заход закрывается: {family.creatives.length} крео уйдут в архив вместе со своими метриками, гипотеза встанет в колонку «Бэклог» и будет ждать новую волну.</p>
          <p>Заведённые в кабинете, но ничего не открутившие крео сначала будут отозваны из Media Library. Цифры открученных сохранятся в архивных карточках — доска их не переписывает.</p>
        </>}
      />}
      {dialog === "lost" && (() => {
        const losable = ["production", "ready_for_upload", "live", "paused_manual", "verdict_pending", "live_test2", "verdict_pending_test2"];
        const moving = family.creatives.filter((creative) => losable.includes(creative.state));
        const measured = moving.filter((creative) => creative.metrics.spend > 0 || creative.metrics.impressions > 0 || creative.verdict);
        return <ConfirmDialog
          title={`${family.id} в лузеры?`}
          confirmLabel="В лузеры"
          tone="danger"
          loading={busy}
          onConfirm={markLost}
          onClose={() => setDialog(null)}
          description={<>
            <p><strong>{family.title ?? family.statement}</strong></p>
            <p>Тест провален, бюджет сюда больше не льём. Гипотеза встанет в «Лузеры», вместе с ней туда уйдут измеренные крео со своими цифрами{measured.length ? ` (${measured.map((creative) => creative.id).join(", ")})` : ""}.</p>
            {moving.length > measured.length && <p>Неизмеренные крео ({moving.length - measured.length}) уйдут в архив: заведённые в кабинете, но ничего не открутившие, сначала будут отозваны из Media Library.</p>}
            <p>Вернуть гипотезу в работу можно из этого же меню, крео при этом останутся в лузерах.</p>
          </>}
        />;
      })()}
      {dialog === "delete" && <ConfirmDialog
        title={`Удалить ${family.id}?`}
        confirmLabel="Удалить"
        loading={busy}
        onConfirm={remove}
        onClose={() => setDialog(null)}
        description={<>
          <p><strong>{family.title ?? family.statement}</strong></p>
          <p>
            {family.creatives.length
              ? `Вместе с гипотезой уйдут ${family.creatives.length} крео и их метрики. `
              : "Крео у гипотезы нет. "}
            Загруженные видео останутся в хранилище — они могли уже уехать в медиатеку кабинета.
            Заметка, из которой выросла гипотеза, сохранится.
          </p>
          <p>Действие необратимо. Если гипотеза уже откручивалась, сервер откажет — её нужно закрывать, а не удалять.</p>
        </>}
      />}
    </AnimatePresence>
  </>;
}

/**
 * Правка гипотезы. Отправляем только изменённые поля: слепой PATCH всем набором
 * упёрся бы в запрет таксономии у распакованной гипотезы, даже когда её никто
 * не трогал.
 */
function EditHypothesis({ family, close }: { family: Hypothesis; close: () => void }) {
  const { data, act, notify } = useAppState();
  const [loading, setLoading] = useState(false);
  const unpacked = family.creatives.length > 0;
  const [form, setForm] = useState({
    title: family.title ?? "",
    statement: family.statement,
    description: family.description ?? "",
    variationNote: family.variationNote ?? "",
    pain: family.pain,
    desire: family.desire,
    angle: family.angle,
    priority: family.priority,
    judge: family.judge,
    product: family.product ?? data.settings.products[0] ?? "Lovmia"
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const patch: Record<string, unknown> = {};
    const trimmed = { title: form.title.trim(), statement: form.statement.trim(), description: form.description.trim() };
    if (trimmed.title !== (family.title ?? "")) patch.title = trimmed.title || null;
    if (trimmed.statement !== family.statement) patch.statement = trimmed.statement;
    if (trimmed.description !== (family.description ?? "")) patch.description = trimmed.description || null;
    // «Что переработали» есть только у вариации: у корня линии сравнивать не с чем.
    if (family.parentId && form.variationNote.trim() !== (family.variationNote ?? "")) {
      patch.variationNote = form.variationNote.trim() || null;
    }
    if (form.priority !== family.priority) patch.priority = form.priority;
    if (form.judge !== family.judge) patch.judge = form.judge;
    if (form.product !== (family.product ?? "")) patch.product = form.product;
    if (!unpacked) {
      if (form.pain !== family.pain) patch.pain = form.pain;
      if (form.desire !== family.desire) patch.desire = form.desire;
      if (form.angle !== family.angle) patch.angle = form.angle;
    }
    if (!Object.keys(patch).length) { close(); return; }
    setLoading(true);
    try {
      await act(`/api/hypotheses/${family.id}/concept`, { method: "PATCH", body: JSON.stringify(patch) }, `${family.id} обновлена`);
      close();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось сохранить", undefined, "danger");
    } finally {
      setLoading(false);
    }
  };

  return <Modal onClose={close}><form className="dialog-form" onSubmit={submit}>
    <span className="eyebrow">{family.id}</span>
    <h2>Править гипотезу</h2>
    <p>Имя механики и утверждение — человеческий текст, его видно на карточках и в реестре. Имена объявлений от этой правки не меняются.</p>
    <label className="field field--full"><span>Имя механики</span>
      <input autoFocus value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Запретный плод: PSA" maxLength={80} />
    </label>
    <label className="field field--full"><span>Утверждение</span>
      <textarea required minLength={8} value={form.statement} onChange={(event) => setForm({ ...form, statement: event.target.value })} placeholder="Если показать…, то…, потому что…" />
    </label>
    <label className="field field--full"><span>Описание</span>
      <textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Почему верим, как выглядит в крео, что считаем успехом" />
    </label>
    {family.parentId && <label className="field field--full"><span>Что переработали относительно {family.parentId}</span>
      <textarea value={form.variationNote} maxLength={500} onChange={(event) => setForm({ ...form, variationNote: event.target.value })}
        placeholder="Хук перенесён на третью реплику, тело без изменений" />
    </label>}
    <div className="form-grid">
      {(["pain", "desire", "angle"] as const).map((key) => <label className="field" key={key}><span>{key}</span>
        <Select value={form[key]} disabled={unpacked} onChange={(event) => setForm({ ...form, [key]: event.target.value })}>
          {data.taxonomy[key].map((item) => <option key={item}>{item}</option>)}
        </Select>
      </label>)}
      <label className="field"><span>Приоритет</span>
        <Select value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value as Hypothesis["priority"] })}>
          {["P0", "P1", "P2", "P3"].map((item) => <option key={item}>{item}</option>)}
        </Select>
      </label>
      <label className="field"><span>Судим по</span>
        <Select value={form.judge} onChange={(event) => setForm({ ...form, judge: event.target.value as Hypothesis["judge"] })}>
          {["cpa", "hook_rate", "hold_rate", "ctr"].map((item) => <option key={item}>{item}</option>)}
        </Select>
      </label>
      {/* Продукт правится всегда: в ad_name он не вшит, метрики не разъедутся.
          Единственный продукт в справочнике поле не показывает — нечего выбирать. */}
      {data.settings.products.length > 1 && <label className="field"><span>Продукт</span>
        <Select value={form.product} onChange={(event) => setForm({ ...form, product: event.target.value })}>
          {data.settings.products.map((item) => <option key={item}>{item}</option>)}
          {family.product && !data.settings.products.includes(family.product) && <option value={family.product}>{family.product}</option>}
        </Select>
      </label>}
    </div>
    {unpacked && <p className="field-note">Боль, желание и угол закрыты: pain вшит в ad_name уже созданных крео, и правка развела бы карточку со статистикой.</p>}
    <div className="dialog-actions">
      <Button type="button" variant="ghost" onClick={close}>Отмена</Button>
      <Button type="submit" variant="primary" loading={loading}>Сохранить</Button>
    </div>
  </form></Modal>;
}

/**
 * Вариация: следующий шаг той же линии (`H-029` → `H-029-V2`).
 *
 * Отдельная карточка со своими крео и своим гейтом — но с ссылкой на родителя,
 * и номер версии стоит прямо в id, поэтому эволюцию видно и на доске, и в имени
 * объявления. Таксономию форма не спрашивает: та же боль и тот же угол и есть
 * «та же линия», другая боль — это новая гипотеза, а не вариация.
 */
function CreateVariation({ family, close }: { family: Hypothesis; close: () => void }) {
  const { act, notify } = useAppState();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ note: "", title: "", statement: family.statement });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      const body: Record<string, unknown> = { note: form.note.trim() };
      if (form.title.trim()) body.title = form.title.trim();
      if (form.statement.trim() !== family.statement) body.statement = form.statement.trim();
      await act(`/api/hypotheses/${family.id}/variations`, { method: "POST", body: JSON.stringify(body) },
        `Вариация ${family.id} создана — она в бэклоге, распакуйте её в пару`);
      close();
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "Не удалось создать вариацию", undefined, "danger");
    } finally {
      setLoading(false);
    }
  };

  return <Modal onClose={close}><form className="dialog-form" onSubmit={submit}>
    <span className="eyebrow">{family.id}</span>
    <h2>Сделать вариацию</h2>
    <p>Утверждение переписано по одной оси, но это та же линия. Новая гипотеза получит следующий номер версии ({family.id.replace(/-V\d+$/, "")}-V…), унаследует боль, желание, угол, шаблон, тон и кастинг — и будет мериться заново, своей парой и своим гейтом.</p>
    <label className="field field--full"><span>Что переработали</span>
      <textarea autoFocus required minLength={8} maxLength={500} value={form.note}
        onChange={(event) => setForm({ ...form, note: event.target.value })}
        placeholder="Хук перенесён на третью реплику, тело без изменений" />
    </label>
    <label className="field field--full"><span>Утверждение</span>
      <textarea required minLength={8} value={form.statement} onChange={(event) => setForm({ ...form, statement: event.target.value })} />
    </label>
    <label className="field field--full"><span>Имя механики</span>
      <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })}
        placeholder={family.title ? `${family.title} · V…` : "Имя новой версии"} maxLength={80} />
    </label>
    <p className="field-note">В кабинете вариация заводится собственным концептом: её деньги считаются отдельно от {family.id}, а сравнить версии между собой можно на доске и в реестре.</p>
    <div className="dialog-actions">
      <Button type="button" variant="ghost" onClick={close}>Отмена</Button>
      <Button type="submit" variant="primary" loading={loading} disabled={form.note.trim().length < 8}>Создать вариацию</Button>
    </div>
  </form></Modal>;
}

/**
 * Объединение: все крео этой гипотезы уезжают в выбранную (получают её
 * следующие номера), кастинг, оригинал и заметки — вместе с ними, пустые
 * шаблон/описание цели заполняются из этой; эта гипотеза удаляется.
 */
function MergeHypothesis({ family, close, onMerged }: { family: Hypothesis; close: () => void; onMerged?: () => void }) {
  const { data, act, notify } = useAppState();
  const [target, setTarget] = useState("");
  const [loading, setLoading] = useState(false);
  const registered = family.creatives.filter((creative) => creative.creativeUid);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!target) return;
    setLoading(true);
    try {
      await act(`/api/hypotheses/${target}/merge`, { method: "POST", body: JSON.stringify({ sourceId: family.id }) });
      notify(`${family.id} влита в ${target}`, registered.length ? "Заведённые крео отозваны из Media Library — после проверки нажмите «В live»" : `крео: ${family.creatives.length}`, "success");
      close();
      onMerged?.();
    } catch (error) {
      notify(error instanceof ApiError ? error.message : "Не удалось объединить", undefined, "danger");
    } finally {
      setLoading(false);
    }
  };
  return <Modal onClose={close}><form className="dialog-form" onSubmit={submit}>
    <span className="eyebrow">{family.id}</span>
    <h2>Объединить с другой гипотезой</h2>
    <p><strong>{family.title ?? family.statement}</strong></p>
    <p>{family.creatives.length ? `${family.creatives.length} крео переедут в выбранную гипотезу следующими номерами и получат её имя объявления. ` : "Крео у гипотезы нет. "}Кастинг, оригинал и заметки уйдут вместе с ними; шаблон и описание заполнят пустые поля цели. Сама {family.id} исчезнет — действие необратимо.</p>
    {registered.length > 0 && <p className="field-note">{registered.map((creative) => creative.id).join(", ")}: заведено в кабинете, но не откручивалось — будет отозвано из Media Library по дороге.</p>}
    <label className="field field--full"><span>В какую гипотезу</span>
      <Select autoFocus value={target} onChange={(event) => setTarget(event.target.value)}>
        <option value="">Выберите гипотезу…</option>
        {data.hypotheses.filter((item) => item.id !== family.id).map((item) => <option key={item.id} value={item.id}>{item.id} · {(item.title ?? item.statement).slice(0, 70)}</option>)}
      </Select>
    </label>
    <div className="dialog-actions">
      <Button type="button" variant="ghost" onClick={close}>Отмена</Button>
      <Button type="submit" variant="primary" loading={loading} disabled={!target}>Объединить</Button>
    </div>
  </form></Modal>;
}
